// Polygon.io (rebranded "Massive") adapter — BYO provider for US stocks.
// Personal plans include an API key; aggregates endpoint is stable and
// simple. Personal-plan data is for the user's own display — exactly the
// client-side use this adapter performs.
// Docs: https://polygon.io/docs/stocks/get_v2_aggs_ticker

import { storageGet, storageSet, storageRemove } from "../storage.js";
import { MarketDataError, type OHLCPoint } from "../market-data.js";
import { rangeToMs, type DataProvider, type CandleResult, type ClosesResult } from "./types.js";

const CREDS_KEY = "providerPolygonCreds";

/** Our interval vocabulary → Polygon {multiplier, timespan}. Pure. */
export function polygonRangeSpec(interval: string): { multiplier: number; timespan: string } | null {
  const match = /^(\d+)(m|h|d|wk|mo)$/.exec(interval.trim());
  if (!match) return null;
  const n = Number(match[1]);
  switch (match[2]) {
    case "m":
      return n % 60 === 0 ? { multiplier: n / 60, timespan: "hour" } : { multiplier: n, timespan: "minute" };
    case "h":
      return { multiplier: n, timespan: "hour" };
    case "d":
      return { multiplier: n, timespan: "day" };
    case "wk":
      return { multiplier: n, timespan: "week" };
    case "mo":
      return { multiplier: n, timespan: "month" };
    default:
      return null;
  }
}

export function polygonSymbol(symbol: string): string | null {
  const s = symbol.trim().toUpperCase();
  return /^[A-Z.]{1,6}$/.test(s) ? s : null;
}

interface PolygonAgg {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

async function getCreds(): Promise<Record<string, string> | null> {
  return await storageGet<Record<string, string> | null>(CREDS_KEY, null);
}

async function fetchAggs(symbol: string, range: string, interval: string): Promise<CandleResult> {
  const creds = await getCreds();
  if (!creds?.["apiKey"]) throw new MarketDataError("Polygon not connected");
  const mapped = polygonSymbol(symbol);
  const spec = polygonRangeSpec(interval);
  if (!mapped || !spec) throw new MarketDataError(`Polygon can't serve ${symbol} @ ${interval}`);

  const end = Date.now();
  const start = end - rangeToMs(range);
  const url =
    `https://api.polygon.io/v2/aggs/ticker/${encodeURIComponent(mapped)}` +
    `/range/${spec.multiplier}/${spec.timespan}/${start}/${end}?adjusted=true&sort=asc&limit=5000&apiKey=${encodeURIComponent(creds["apiKey"])}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (response.status === 401 || response.status === 403) throw new MarketDataError("Polygon rejected the key — check it in Settings → Data.");
    if (!response.ok) throw new MarketDataError(`Polygon aggs failed: ${response.status}`);
    const body = (await response.json()) as { results?: PolygonAgg[] };
    const candles: OHLCPoint[] = (body.results ?? []).map((a) => ({
      date: new Date(a.t),
      open: a.o,
      high: a.h,
      low: a.l,
      close: a.c,
      volume: a.v ?? 0,
    }));
    const last = candles[candles.length - 1];
    return { candles, currentPrice: last?.close };
  } finally {
    clearTimeout(timeout);
  }
}

export const polygonProvider: DataProvider = {
  id: "polygon",
  displayName: "Polygon / Massive (your plan)",
  assetClasses: ["Stocks"],
  realtime: true, // on their paid personal plans; free tier is EOD/delayed
  setup: {
    signupUrl: "https://polygon.io/",
    keyLabel: "API key",
    costNote: "Your own Polygon/Massive plan (free tier is delayed; paid tiers are live)",
  },
  byo: {
    fields: [{ key: "apiKey", label: "API key", type: "password" }],
    getCreds,
    async setCreds(creds) {
      if (creds) await storageSet(CREDS_KEY, creds);
      else await storageRemove(CREDS_KEY);
    },
    async verify(creds) {
      try {
        const response = await fetch(
          `https://api.polygon.io/v2/aggs/ticker/AAPL/prev?adjusted=true&apiKey=${encodeURIComponent(creds["apiKey"] ?? "")}`
        );
        if (response.ok) return { ok: true };
        return { ok: false, message: response.status === 401 || response.status === 403 ? "Polygon rejected that key." : `Polygon error ${response.status}.` };
      } catch {
        return { ok: false, message: "Could not reach Polygon." };
      }
    },
  },
  candles: fetchAggs,
  async closes(symbol, range, interval): Promise<ClosesResult> {
    const { candles, currentPrice } = await fetchAggs(symbol, range, interval);
    return { closes: candles.map((c) => c.close), regularMarketPrice: currentPrice };
  },
};
